/**
 * Catálogo Fairtrade — normalização da API api.xatblog.net/prices.
 */

import {
  getPowerCategoryAccentHex,
  getPowerCategorySmileyCode,
} from '@/lib/power-category-theme';
import {
  API_CATEGORY_DEFS,
  getPowerCategoryLabel,
  resolveApiFilterCategory,
  type PowerApiFilterCategory,
} from '@/lib/powers-data';

export type FairtradeStatusFilter = 'all' | 'limited' | 'unlimited';

export type FairtradePowerStatus = 'Limited' | 'Unlimited';

export type FairtradePowerFlags = {
  limited: boolean;
  allpowers: boolean;
  isNew: boolean;
  isLatest: boolean;
  isEpic: boolean;
  isGroup: boolean;
  isCollection: boolean;
};

export type FairtradePowerRow = {
  id: number;
  name: string;
  slug: string;
  title: string;
  /** Type bruto da API de prices (ex.: yellow, superfarm, supers). */
  type: string;
  /** Categoria canónica de `/powers` (mesma resolução da API). */
  category: PowerApiFilterCategory;
  /** Label da categoria canónica (ex.: Smilies, Function). */
  typeLabel: string;
  minXats: number;
  maxXats: number;
  minDays: number;
  maxDays: number;
  storePrice: string;
  released: string | null;
  releasedInt: number;
  flags: FairtradePowerFlags;
  statusLabel: FairtradePowerStatus;
  categories: string[];
};

/** IDs das categorias de `/powers` (exceto Limitados/Ilimitados). */
export const FAIRTRADE_API_CATEGORY_IDS: ReadonlySet<string> = new Set(
  API_CATEGORY_DEFS.map((d) => d.id)
);

function capitalizeType(type: string): string {
  if (!type) return 'Other';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export function fairtradeTypeLabel(type: string): string {
  return capitalizeType(type.trim().toLowerCase());
}

function num(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function normalizeFairtradePowerRow(
  raw: Record<string, unknown>
): FairtradePowerRow | null {
  const name = str(raw.name).toLowerCase();
  if (!name) return null;

  const type = str(raw.type).toLowerCase() || 'other';
  // Aces têm secção própria em /aces — não entram no Fairtrade.
  if (type === 'ace') return null;

  const id = num(raw.id);
  const categories = Array.isArray(raw.categories)
    ? raw.categories.map(String).map((c) => c.trim()).filter(Boolean)
    : [];

  const limited = categories.includes('is_limited');
  const title = str(raw.description) || str(raw.description_full) || name;
  const category = resolveApiFilterCategory(type, categories);

  return {
    id,
    name,
    slug: name,
    title,
    type,
    category,
    typeLabel: getPowerCategoryLabel(category),
    minXats: num(raw.fairtrade_min_xats),
    maxXats: num(raw.fairtrade_max_xats),
    minDays: num(raw.fairtrade_min_days),
    maxDays: num(raw.fairtrade_max_days),
    storePrice: str(raw.store_price),
    released: str(raw.released) || null,
    releasedInt: num(raw.released_int),
    flags: {
      limited,
      allpowers: categories.includes('is_allpowers'),
      isNew: categories.includes('is_new'),
      isLatest: categories.includes('is_latest'),
      isEpic: categories.includes('is_epic'),
      isGroup: categories.includes('is_group'),
      isCollection: categories.includes('is_collection'),
    },
    statusLabel: limited ? 'Limited' : 'Unlimited',
    categories,
  };
}

export function normalizeFairtradePricesResponse(data: unknown): FairtradePowerRow[] {
  if (!data || typeof data !== 'object') return [];
  const root = data as { code?: number; prices?: unknown; data?: unknown };

  let list: unknown = root.prices;
  if (!Array.isArray(list) && Array.isArray(root.data)) list = root.data;
  if (!Array.isArray(list) && Array.isArray(data)) list = data;
  if (!Array.isArray(list)) return [];

  // Aceita code 200 ou ausência de code (alguns proxies só devolvem o array).
  if (root.code != null && root.code !== 200) return [];

  const rows: FairtradePowerRow[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const row = normalizeFairtradePowerRow(item as Record<string, unknown>);
    if (row) rows.push(row);
  }
  return rows;
}

export function collectFairtradeTypes(rows: FairtradePowerRow[]): string[] {
  const set = new Set<string>();
  for (const row of rows) {
    if (row.category) set.add(row.category);
  }
  return Array.from(set).sort((a, b) =>
    getPowerCategoryLabel(a).localeCompare(getPowerCategoryLabel(b), 'pt', {
      sensitivity: 'base',
    })
  );
}

export type FairtradeCategoryStat = {
  type: string;
  label: string;
  count: number;
  sumMinXats: number;
  sumMaxXats: number;
  sumMinDays: number;
  sumMaxDays: number;
  isCollection: boolean;
  /** Shortname do smiley/power usado no ícone do card. */
  sampleIcon: string;
  /** Cor do card — alinhada a `/powers` nas categorias. */
  accentHex: string;
};

const COLLECTION_ACCENT = getPowerCategoryAccentHex('collection');

/** Power mestre da coleção (ex.: superbf, superanime) — não entra na lista Fairtrade. */
export function isFairtradeCollectionHub(row: FairtradePowerRow): boolean {
  return row.flags.isCollection || row.type === 'supers';
}

/**
 * Chave da família de coleção:
 * - power mestre (`is_collection` / type `supers`) → `name` (ex.: superfarm)
 * - members com type `super*` → o próprio type
 */
export function fairtradeCollectionFamilyKey(row: FairtradePowerRow): string | null {
  if (isFairtradeCollectionHub(row)) {
    return row.name || null;
  }
  if (row.type.startsWith('super') && row.type !== 'supers') {
    return row.type;
  }
  return null;
}

/**
 * Smiley do card de coleção = o próprio power da coleção
 * (superbunny → superbunny, superbf → superbf, …).
 */
export function fairtradeCollectionMainSmiley(collectionKey: string): string {
  return collectionKey.trim().toLowerCase();
}

/** Badge na lista: com filtro de coleção activo, mostra o nome da coleção. */
export function getFairtradeRowCategoryDisplay(
  row: FairtradePowerRow,
  activeFilter = ''
): { categoryKey: string; label: string } {
  const filter = activeFilter.trim().toLowerCase();
  const family = fairtradeCollectionFamilyKey(row);

  if (
    filter &&
    !FAIRTRADE_API_CATEGORY_IDS.has(filter) &&
    family &&
    family === filter
  ) {
    return {
      categoryKey: 'collection',
      label: fairtradeTypeLabel(family),
    };
  }

  return {
    categoryKey: row.category,
    label: row.typeLabel,
  };
}

type AggDraft = Omit<FairtradeCategoryStat, 'accentHex'> & { sampleId: number };

function emptyAgg(
  type: string,
  label: string,
  isCollection: boolean,
  sampleIcon: string,
  sampleId: number
): AggDraft {
  return {
    type,
    label,
    count: 0,
    sumMinXats: 0,
    sumMaxXats: 0,
    sumMinDays: 0,
    sumMaxDays: 0,
    isCollection,
    sampleIcon,
    sampleId,
  };
}

function addRowToAgg(draft: AggDraft, row: FairtradePowerRow, iconOverride?: string) {
  draft.count += 1;
  draft.sumMinXats += row.minXats;
  draft.sumMaxXats += row.maxXats;
  draft.sumMinDays += row.minDays;
  draft.sumMaxDays += row.maxDays;
  if (iconOverride) {
    draft.sampleIcon = iconOverride;
  } else if (row.id > draft.sampleId && row.name) {
    draft.sampleId = row.id;
    draft.sampleIcon = row.name;
  }
}

/**
 * Categorias = `API_CATEGORY_DEFS` de `/powers`.
 * Coleções = um card por família (superfarm, superanime, …) com smiley principal.
 */
export function computeFairtradeCategoryStats(
  rows: FairtradePowerRow[]
): FairtradeCategoryStat[] {
  const byCategory = new Map<string, AggDraft>();

  for (const def of API_CATEGORY_DEFS) {
    byCategory.set(
      def.id,
      emptyAgg(
        def.id,
        def.label,
        false,
        getPowerCategorySmileyCode(def.id) ?? def.id,
        -1
      )
    );
  }

  const byCollection = new Map<string, AggDraft>();

  for (const row of rows) {
    const catDraft = byCategory.get(row.category);
    if (catDraft) {
      // Ícone fixo das categorias de `/powers` — não trocar pelo power amostrado.
      addRowToAgg(catDraft, row, getPowerCategorySmileyCode(row.category) ?? catDraft.sampleIcon);
    }

    const family = fairtradeCollectionFamilyKey(row);
    if (!family) continue;

    const collectionIcon = fairtradeCollectionMainSmiley(family);
    let colDraft = byCollection.get(family);
    if (!colDraft) {
      colDraft = emptyAgg(
        family,
        fairtradeTypeLabel(family),
        true,
        collectionIcon,
        -1
      );
      byCollection.set(family, colDraft);
    }
    // Cards: soma só dos powers da coleção — não do hub (superbf, etc.).
    if (isFairtradeCollectionHub(row)) {
      colDraft.sampleIcon = collectionIcon;
      continue;
    }
    addRowToAgg(colDraft, row, collectionIcon);
  }

  const categories: FairtradeCategoryStat[] = API_CATEGORY_DEFS.flatMap((def) => {
    // Collection tem secção própria (cards por família) — não entra nas categorias.
    if (def.id === 'collection') return [];
    const draft = byCategory.get(def.id);
    if (!draft || draft.count === 0) return [];
    const { sampleId: _id, ...rest } = draft;
    return [
      {
        ...rest,
        sampleIcon: getPowerCategorySmileyCode(def.id) ?? rest.sampleIcon,
        accentHex: getPowerCategoryAccentHex(def.id),
      },
    ];
  });

  const collections: FairtradeCategoryStat[] = Array.from(byCollection.values())
    .sort((a, b) => a.label.localeCompare(b.label, 'pt', { sensitivity: 'base' }))
    .map((draft) => {
      const { sampleId: _id, ...rest } = draft;
      return {
        ...rest,
        sampleIcon: fairtradeCollectionMainSmiley(draft.type),
        accentHex: COLLECTION_ACCENT,
      };
    });

  return [...categories, ...collections];
}

export function formatFairtradeStatNumber(n: number): string {
  if (!n) return '0';
  return n.toLocaleString('en-US');
}

export function formatFairtradeStatRange(min: number, max: number): string {
  if (!min && !max) return '—';
  if (min === max || !min || !max) return formatFairtradeStatNumber(min || max);
  return `${formatFairtradeStatNumber(min)} - ${formatFairtradeStatNumber(max)}`;
}

/** Preços agregados por família de coleção (iguais aos cards). */
export type FairtradeCollectionPriceStats = {
  minXats: number;
  maxXats: number;
  minDays: number;
  maxDays: number;
};

export function buildFairtradeCollectionPriceIndex(
  rows: FairtradePowerRow[]
): Map<string, FairtradeCollectionPriceStats> {
  const map = new Map<string, FairtradeCollectionPriceStats>();

  for (const row of rows) {
    if (isFairtradeCollectionHub(row)) continue;
    const family = fairtradeCollectionFamilyKey(row);
    if (!family) continue;
    const prev = map.get(family) ?? {
      minXats: 0,
      maxXats: 0,
      minDays: 0,
      maxDays: 0,
    };
    prev.minXats += row.minXats;
    prev.maxXats += row.maxXats;
    prev.minDays += row.minDays;
    prev.maxDays += row.maxDays;
    map.set(family, prev);
  }

  return map;
}

/**
 * Preços exibidos na lista / wishlist = sempre o do power individual.
 * Totais por coleção ficam nos cards (`computeFairtradeCategoryStats`).
 */
export function getFairtradeRowDisplayPrices(
  row: FairtradePowerRow,
  _collectionPrices?: Map<string, FairtradeCollectionPriceStats>
): {
  minXats: number;
  maxXats: number;
  minDays: number;
  maxDays: number;
  fromCollection: boolean;
} {
  return {
    minXats: row.minXats,
    maxXats: row.maxXats,
    minDays: row.minDays,
    maxDays: row.maxDays,
    fromCollection: false,
  };
}

/** Destaque: is_latest → primeiro is_new com preço → null. */
export function pickFeaturedFairtradePower(
  rows: FairtradePowerRow[]
): FairtradePowerRow | null {
  const latest = rows.find((r) => r.flags.isLatest);
  if (latest) return latest;

  const withPrice = (r: FairtradePowerRow) =>
    r.maxXats > 0 || r.minXats > 0 || r.maxDays > 0 || r.minDays > 0;

  const neu = rows.find((r) => r.flags.isNew && withPrice(r));
  return neu ?? null;
}

export function formatFairtradeXatsRange(min: number, max: number): string {
  if (!min && !max) return '—';
  if (min === max) return `${min}`;
  if (!min) return `${max}`;
  if (!max) return `${min}`;
  return `${min} – ${max}`;
}

export function formatFairtradeDaysRange(min: number, max: number): string {
  if (!min && !max) return '—';
  if (min === max) return `${min}`;
  if (!min) return `${max}`;
  if (!max) return `${min}`;
  return `${min} – ${max}`;
}

/** Paleta restrita de badges por type (legado). Preferir categoria canónica. */
export function fairtradeTypeBadgeClass(type: string): string {
  const t = type.toLowerCase();
  if (t === 'function') {
    return 'border-sky-200 bg-sky-50 text-sky-800';
  }
  if (t === 'smiley' || t.includes('smiley')) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  }
  if (t === 'yellow') {
    return 'border-amber-200 bg-amber-50 text-amber-900';
  }
  if (t === 'epic' || t.includes('epic')) {
    return 'border-violet-200 bg-violet-50 text-violet-800';
  }
  if (t.includes('color')) {
    return 'border-indigo-200 bg-indigo-50 text-indigo-800';
  }
  if (t.includes('super') || t === 'stickers') {
    return 'border-blue-200 bg-blue-50 text-blue-800';
  }
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

export function fairtradeStatusBadgeClass(status: FairtradePowerStatus): string {
  if (status === 'Limited') {
    return 'border-orange-200 bg-orange-50 text-orange-800';
  }
  return 'border-sky-200 bg-sky-50 text-sky-800';
}
